import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import format from "date-fns/format";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import { createSupabaseClient } from "../../../hooks/useSupabase";

type RawSpecialtyRow = {
  center_user_id: string;
  specialty: string;
};

type RawCutoffRow = {
  center_user_id: string;
  date: string;
  type: "available" | "unavailable" | "cutoff_time";
  cutoff_minutes_from_midnight: number | null;
};

const getRadCutoffMinutes = (
  radId: string,
  dateISO: string,
  cutoffRows: RawCutoffRow[],
): number => {
  const row = cutoffRows.find(
    (r) => r.center_user_id === radId && r.date === dateISO,
  );
  if (!row || row.type === "available") return Infinity;
  if (row.type === "unavailable") return 0;
  return row.cutoff_minutes_from_midnight ?? Infinity;
};

const useStatAvailability = (params: {
  centerId: string | undefined;
  monthDate: Date;
  requiredSpecialties: string[];
  enabled: boolean;
}): { statCutoffs: Record<string, number> } => {
  const { centerId, monthDate, requiredSpecialties, enabled } = params;

  const supabase = useMemo(() => createSupabaseClient(), []);

  const monthISO = format(monthDate, "yyyy-MM");

  const { data: statCutoffs = {} } = useQuery<Record<string, number>>({
    queryKey: ["stat-availability", centerId, monthISO, requiredSpecialties],
    queryFn: async () => {
      if (!centerId) return {};

      const monthStart = format(startOfMonth(monthDate), "yyyy-MM-dd");
      const monthEnd = format(endOfMonth(monthDate), "yyyy-MM-dd");

      const { data: specialtiesData, error: specialtiesError } = await supabase
        .from("radiologist_specialties")
        .select("center_user_id, specialty")
        .eq("center_id", centerId)
        .in("specialty", requiredSpecialties);

      if (specialtiesError) throw new Error(specialtiesError.message);

      const rows = (specialtiesData ?? []) as unknown as RawSpecialtyRow[];

      // Group rads by specialty
      const radsBySpecialty = requiredSpecialties.reduce<
        Record<string, string[]>
      >((acc, specialty) => {
        acc[specialty] = rows
          .filter((r) => r.specialty === specialty)
          .map((r) => r.center_user_id);
        return acc;
      }, {});

      // If any required specialty has no rads, all dates are unavailable
      const noRadsForSomeSpecialty = requiredSpecialties.some(
        (s) => radsBySpecialty[s].length === 0,
      );
      if (noRadsForSomeSpecialty) {
        const days = eachDayOfInterval({
          start: startOfMonth(monthDate),
          end: endOfMonth(monthDate),
        });
        return Object.fromEntries(
          days.map((d) => [format(d, "yyyy-MM-dd"), 0]),
        );
      }

      const allRadIds = Array.from(new Set(rows.map((r) => r.center_user_id)));

      const { data: cutoffData, error: cutoffError } = await supabase
        .from("radiologist_read_cutoffs")
        .select("center_user_id, date, type, cutoff_minutes_from_midnight")
        .eq("center_id", centerId)
        .in("center_user_id", allRadIds)
        .gte("date", monthStart)
        .lte("date", monthEnd);

      if (cutoffError) throw new Error(cutoffError.message);

      const cutoffRows = (cutoffData ?? []) as unknown as RawCutoffRow[];

      const days = eachDayOfInterval({
        start: startOfMonth(monthDate),
        end: endOfMonth(monthDate),
      });

      return days.reduce<Record<string, number>>((result, day) => {
        const dateISO = format(day, "yyyy-MM-dd");

        // effective_cutoff = min over specialties of (max cutoff across its rads)
        const effectiveCutoff = requiredSpecialties.reduce(
          (minCutoff, specialty) => {
            const radIds = radsBySpecialty[specialty];
            const specialtyCutoff = Math.max(
              ...radIds.map((id) =>
                getRadCutoffMinutes(id, dateISO, cutoffRows),
              ),
            );
            return Math.min(minCutoff, specialtyCutoff);
          },
          Infinity,
        );

        // Only include dates where cutoff is not Infinity (no filtering needed for full coverage)
        if (effectiveCutoff !== Infinity) {
          return { ...result, [dateISO]: effectiveCutoff };
        }
        return result;
      }, {});
    },
    enabled: enabled && !!centerId && requiredSpecialties.length > 0,
  });

  return { statCutoffs };
};

export default useStatAvailability;
